export interface PersistedBannerImage {
  url: string;
  title: string;
  description: string;
  order: number;
  mobile_url?: string | null;
  mobile_custom_url?: string | null;
  optimized?: boolean;
  metadata?: unknown;
}

export interface BannerUploadEntry extends Partial<PersistedBannerImage> {
  uid?: string;
  name?: string;
  status?: string;
  originFileObj?: unknown;
  thumbUrl?: string;
}

// Les uploads desktop sont ajoutés après les images persistées par l'API.
// Une suppression doit donc déplacer aussi les associations mobiles suivantes.
export const removeDesktopBanner = (
  desktopBanners: BannerUploadEntry[], mobileBanners: BannerUploadEntry[], index: number
) => ({
  desktop: desktopBanners.filter((_, position) => position !== index)
    .map((banner, order) => ({ ...banner, order })),
  mobile: mobileBanners.filter((banner) => banner.order !== index)
    .map((banner) => ({ ...banner, order: Number(banner.order) > index ? Number(banner.order) - 1 : banner.order })),
});

export const mobileBannerUploadMetadata = (
  desktopBanners: BannerUploadEntry[], mobileBanners: BannerUploadEntry[]
) => mobileBanners.filter((banner) => banner.originFileObj && banner.status !== "done")
  .map((banner) => {
    const position = Number(banner.order);
    const desktop = desktopBanners[position];
    if (!desktop) throw new Error("La bannière associée à cette version mobile n'existe plus.");
    const persistedCount = desktopBanners.filter((entry) => entry.status === "done").length;
    const targetIndex = desktop.status === "done"
      ? desktopBanners.slice(0, position).filter((entry) => entry.status === "done").length
      : persistedCount + desktopBanners.slice(0, position).filter((entry) => entry.status !== "done").length;
    return { targetIndex };
  });

const getBannerOrder = (banner: BannerUploadEntry, fallback: number) =>
  Number.isInteger(banner.order) && Number(banner.order) >= 0
    ? Number(banner.order)
    : fallback;

export const normalizeMobileBannerOrders = (
  fileList: BannerUploadEntry[],
  desktopBannerCount: number
): BannerUploadEntry[] => {
  const usedOrders = new Set<number>();

  return fileList.map((file, index) => {
    const requestedOrder = getBannerOrder(file, -1);
    const hasValidOrder =
      requestedOrder >= 0 &&
      requestedOrder < desktopBannerCount &&
      !usedOrders.has(requestedOrder);

    if (hasValidOrder) {
      usedOrders.add(requestedOrder);
      return file;
    }

    let nextOrder = 0;
    while (nextOrder < desktopBannerCount && usedOrders.has(nextOrder)) {
      nextOrder += 1;
    }

    const assignedOrder =
      nextOrder < desktopBannerCount ? nextOrder : Math.max(index, 0);
    usedOrders.add(assignedOrder);

    return { ...file, order: assignedOrder };
  });
};

export const buildPersistedBannerImages = (
  desktopBanners: BannerUploadEntry[],
  mobileBanners: BannerUploadEntry[]
): PersistedBannerImage[] => {
  const mobileByOrder = new Map<number, BannerUploadEntry>();

  mobileBanners.forEach((banner, index) => {
    mobileByOrder.set(getBannerOrder(banner, index), banner);
  });

  return desktopBanners
    .map((banner, index) => ({
      banner,
      sourceOrder: getBannerOrder(banner, index),
    }))
    .filter(({ banner }) => banner.status === "done")
    .sort((left, right) => left.sourceOrder - right.sourceOrder)
    .map(({ banner, sourceOrder }, index) => {
      const mobileBanner = mobileByOrder.get(sourceOrder);
      const persistedBanner: PersistedBannerImage = {
        url: banner.url || "",
        title: banner.title || "",
        description: banner.description || "",
        order: index,
        mobile_custom_url: mobileBanner?.url || null,
      };

      if (Object.prototype.hasOwnProperty.call(banner, "mobile_url")) {
        persistedBanner.mobile_url = banner.mobile_url || null;
      }
      if (Object.prototype.hasOwnProperty.call(banner, "optimized")) {
        persistedBanner.optimized = banner.optimized;
      }
      if (Object.prototype.hasOwnProperty.call(banner, "metadata")) {
        persistedBanner.metadata = banner.metadata;
      }

      return persistedBanner;
    });
};
